import dayjs, { Dayjs } from "dayjs";
import { formatInteger, formatPercent } from "../formatters";

// The five cumulative stages, in funnel order. `step` is the Lead Funnel
// query's output value; `state` is the capitalized value the Brand Status
// Query's `state` input (and its `current_state` column) uses.
export interface BrandStage {
  step: string;
  state: string;
  name: string;
  color: string;
  // Drill-down header icon box tint (from the mockup's stage meta).
  accent: string;
  accentBackground: string;
}

export const BRAND_STAGES: BrandStage[] = [
  { step: "paid", state: "Paid", name: "Paid", color: "#CE93D8", accent: "#2E7D32", accentBackground: "#E8F5E9" },
  {
    step: "application",
    state: "Application",
    name: "Application",
    color: "#BA68C8",
    accent: "#1565C0",
    accentBackground: "#E3F2FD",
  },
  {
    step: "underwriting",
    state: "Underwriting",
    name: "Underwriting",
    color: "#AB47BC",
    accent: "#E65100",
    accentBackground: "#FFF3E0",
  },
  {
    step: "approval",
    state: "Approval",
    name: "Approval",
    color: "#8E24AA",
    accent: "#7B1FA2",
    accentBackground: "#F3E5F5",
  },
  { step: "funded", state: "Funded", name: "Funded", color: "#6A1B9A", accent: "#1B5E20", accentBackground: "#DCEDC8" },
];

export const ALL_BRANDS_UID = "0";

export type StageCounts = number[];

export interface BrandStagesRange {
  start: string;
  end: string;
}

// Default range: first day of the current quarter through today.
export function getDefaultBrandStagesRange(): [Dayjs, Dayjs] {
  const today = dayjs();
  const quarterStart = today.month(Math.floor(today.month() / 3) * 3).startOf("month");
  return [quarterStart, today];
}

// Maps Lead Funnel's `query_output` by `step` (never by array position -
// the query's UNION ALL comes back in no fixed order). A step with no
// matching leads produces no row at all (COUNT(*) ... GROUP BY 1), so a
// missing step is a 0.
export function mapLeadFunnelRows(rows: any): StageCounts {
  const list = Array.isArray(rows) ? rows : [];
  return BRAND_STAGES.map((stage) => {
    const row = list.find((item: any) => String(item?.step).toLowerCase() === stage.step);
    const users = Number(row?.users);
    return Number.isFinite(users) ? users : 0;
  });
}

// Spec Section 2 derivations. All return null where the spec displays "—".
export function retainedPercent(counts: StageCounts, index: number): number | null {
  return counts[0] ? (counts[index] / counts[0]) * 100 : null;
}

export function ofPreviousPercent(counts: StageCounts, index: number): number | null {
  if (index === 0) return null;
  return counts[index - 1] ? (counts[index] / counts[index - 1]) * 100 : null;
}

export function vsPreviousPercent(counts: StageCounts, index: number): number | null {
  if (index === 0) return null;
  return counts[index - 1] ? ((counts[index] - counts[index - 1]) / counts[index - 1]) * 100 : null;
}

export function formatSignedPercent(value: number): string {
  const rounded = Math.round(value);
  return `${rounded >= 0 ? "+" : "−"}${Math.abs(rounded)}%`;
}

export function formatOptionalPercent(value: number | null): string {
  return value === null ? "—" : formatPercent(value, 1);
}

export function formatOptionalSignedPercent(value: number | null): string {
  return value === null ? "—" : formatSignedPercent(value);
}

export function formatLeadCount(count: number): string {
  return `${formatInteger(count)} lead${count === 1 ? "" : "s"}`;
}

export interface BiggestDropOff {
  index: number;
  lost: number;
  change: number | null;
}

// Largest ABSOLUTE loss from Paid to any later stage (Application,
// Underwriting, Approval or Funded) - not the largest percentage drop, and
// not between consecutive stages. `index` is the stage the loss is measured
// to; ties go to the earliest such stage, i.e. where the leads were lost.
// Null when there are no paid leads at all.
export function findBiggestDropOff(counts: StageCounts): BiggestDropOff | null {
  const paid = counts[0];
  if (!paid) return null;
  let biggest: BiggestDropOff | null = null;
  for (let index = 1; index < counts.length; index++) {
    const lost = paid - counts[index];
    if (biggest === null || lost > biggest.lost) {
      biggest = { index, lost, change: ((counts[index] - paid) / paid) * 100 };
    }
  }
  return biggest;
}

export interface BrandStagesBrand {
  brandUid: string;
  brandName: string;
}

// The matrix's brand columns come from the Brand Status Query's Paid rows
// for all brands in the selected range - the distinct brand_uid/brand_name
// pairs among them.
export function mapBrandsFromStatusRows(rows: any): BrandStagesBrand[] {
  const list = Array.isArray(rows) ? rows : [];
  const brandsByUid = new Map<string, string>();
  list.forEach((row: any) => {
    const brandUid = row?.brand_uid;
    if (brandUid && !brandsByUid.has(brandUid)) {
      brandsByUid.set(brandUid, row?.brand_name ?? brandUid);
    }
  });
  return Array.from(brandsByUid, ([brandUid, brandName]) => ({ brandUid, brandName }));
}

export function drillCacheKey(brandUid: string, state: string, range: BrandStagesRange): string {
  return `${brandUid}|${state}|${range.start}|${range.end}`;
}

// Index into BRAND_STAGES of a lead's furthest stage. A paid lead whose
// current_state isn't one of the four post-paid stages counts as Paid.
export function stageIndexFromCurrentState(currentState: any): number {
  const index = BRAND_STAGES.findIndex(
    (stage, stageIndex) => stageIndex > 0 && stage.state.toLowerCase() === String(currentState ?? "").toLowerCase()
  );
  return index === -1 ? 0 : index;
}

export interface BrandStageLeadRow {
  id: number | string;
  businessName: string;
  userUniqueId: string;
  brandName: string;
  acquiredAt: string | null;
  reportedAt: string | null;
  income: number;
  stageIndex: number;
}

// Maps one row of the Brand Status Query's `query_output` (raw field names,
// e.g. `id` comes back as a float like 327.0) into the drill-down table's
// shape. Only the fields the visible table needs (spec Section 3.5) - the
// export uses the raw rows directly.
export function mapBrandStageLeadApiRow(raw: any): BrandStageLeadRow {
  const id = Number(raw?.id);
  return {
    id: Number.isFinite(id) ? Math.round(id) : raw?.id ?? "",
    businessName: raw?.business_name ?? "",
    userUniqueId: raw?.user_unique_id ?? "",
    brandName: raw?.brand_name ?? "",
    acquiredAt: raw?.date_of_acquisition ?? null,
    reportedAt: raw?.reported_at ?? null,
    income: Number(raw?.income_from_brand) || 0,
    stageIndex: stageIndexFromCurrentState(raw?.current_state),
  };
}

export type BrandStageLeadSortKey = "id" | "businessName" | "brandName" | "reportedAt" | "income" | "stageIndex";

export interface BrandStageLeadSort {
  key: BrandStageLeadSortKey;
  direction: "asc" | "desc";
}

// Spec default: newest reported first.
export const DEFAULT_BRAND_STAGE_LEAD_SORT: BrandStageLeadSort = { key: "reportedAt", direction: "desc" };

function leadSortValue(row: BrandStageLeadRow, key: BrandStageLeadSortKey): number | string | null {
  if (key === "reportedAt") return row.reportedAt ? dayjs(row.reportedAt).valueOf() : null;
  const value = row[key];
  return value === "" ? null : value;
}

// Sorts mapped rows (each paired with its raw row, so EXPORT LIST can
// follow the on-screen order). Empty values always sort last.
export function sortBrandStageLeads<T extends { row: BrandStageLeadRow }>(entries: T[], sort: BrandStageLeadSort): T[] {
  const multiplier = sort.direction === "asc" ? 1 : -1;
  return entries.slice().sort((a, b) => {
    const aValue = leadSortValue(a.row, sort.key);
    const bValue = leadSortValue(b.row, sort.key);
    if (aValue === null || bValue === null) return aValue === bValue ? 0 : aValue === null ? 1 : -1;
    if (typeof aValue === "string" || typeof bValue === "string") {
      return String(aValue).localeCompare(String(bValue)) * multiplier;
    }
    return (aValue - bValue) * multiplier;
  });
}

// Every Brand Status Query column, in the query's own SELECT order - EXPORT
// LIST must contain all of them, including the ones the table hides.
export const BRAND_STATUS_EXPORT_COLUMNS = [
  "id",
  "brand_uid",
  "user_unique_id",
  "report_status",
  "reported_at",
  "date_of_acquisition",
  "adgroup",
  "campaign",
  "traffic_source",
  "adid",
  "ad_platform_click_id",
  "time_in_business",
  "monthly_revenue",
  "credit_rating",
  "borrow_amount",
  "business_industry",
  "loan_purpose",
  "business_name",
  "brand_name",
  "ip_address",
  "income_from_brand",
  "brand_lead_status",
  "application",
  "underwriting",
  "approval",
  "funded",
  "questionnaire_fill_time",
  "lead_scoring",
  "loan_approval_likelihood",
  "created",
  "modified",
  "current_state",
];

export function toBrandStatusExportRow(raw: any): Array<string | number> {
  return BRAND_STATUS_EXPORT_COLUMNS.map((column) => raw?.[column] ?? "");
}

export const BRAND_STAGES_MATRIX_CSV_HEADERS = [
  "Brand",
  ...BRAND_STAGES.flatMap((stage, index) => (index === 0 ? [stage.name] : [stage.name, `${stage.name} % retained`])),
];

export function toBrandStagesMatrixCsvRow(brandName: string, counts: StageCounts): Array<string | number> {
  return [
    brandName,
    ...counts.flatMap((count, index) =>
      index === 0 ? [count] : [count, formatOptionalPercent(retainedPercent(counts, index))]
    ),
  ];
}
